import { forwardRef } from "react";
import { optimizedImage } from "@/lib/image";

const assetMap: Record<string, string> = import.meta.glob("/src/assets/*", {
  eager: true,
  import: "default",
});

export function resolveAsset(path?: string | null): string {
  if (!path) return "";
  if (assetMap[path]) return assetMap[path];
  return path;
}

interface AssetImageProps {
  src?: string | null;
  alt: string;
  className?: string;
  loading?: "lazy" | "eager";
  /** Target rendered width in CSS pixels — used to request a right-sized
   * WebP from the image proxy when src is on the legacy WordPress origin. */
  width?: number;
  /** Optional rendered height in CSS pixels (for fixed-aspect crops). */
  height?: number;
  fetchPriority?: "high" | "low" | "auto";
}

export const AssetImage = forwardRef<HTMLImageElement, AssetImageProps>(
  ({ src, alt, className, loading = "lazy", width = 800, height, fetchPriority }, ref) => {
    const resolved = resolveAsset(src);
    if (!resolved) {
      return (
        <div
          role="img"
          aria-label={alt}
          className={(className ?? "") + " flex items-center justify-center bg-gradient-to-br from-primary/15 via-accent/10 to-primary/5 text-primary/40"}
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="h-10 w-10">
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <circle cx="8.5" cy="9.5" r="1.5" />
            <path d="M21 15l-5-5L5 21" />
          </svg>
        </div>
      );
    }
    const optimized = optimizedImage(resolved, { width, height });
    return (
      <img
        ref={ref}
        src={optimized}
        alt={alt}
        width={width}
        height={height}
        loading={loading}
        decoding="async"
        // @ts-expect-error - fetchpriority is a valid HTML attribute, React typings lag
        fetchpriority={fetchPriority}
        className={className}
      />
    );
  },
);
AssetImage.displayName = "AssetImage";
